<template>
  <div class="row p-2" v-if="selectedConversation">
    <div class="col-12">
      <div class="d-flex"></div>
        <textarea
          rows="3"
          ref="message"
          class="form-control"
          id="inlineFormInputGroup"
          placeholder="พิมพ์ข้อความ..."
          v-model="message"
          @keydown.shift.enter="sendMessage"
        />
      <!-- per-platform text limit: no DOM node for an empty draft, and none at any length when the
           platform has no limit. Visible from the first character otherwise, so the ceiling the
           admin is writing against is always legible. -->
      <div v-if="showLimitMeter" class="limit-meter" :class="'limit-meter--' + limitState">
        <span v-if="isOverTextLimit" :id="warningLineId" class="limit-meter__warn"
              role="status" aria-live="polite">{{ warningText }}</span>
        <span class="limit-meter__count" aria-hidden="true">
          <svg v-if="isOverTextLimit" class="limit-meter__glyph" viewBox="0 0 24 24" width="14" height="14"
               aria-hidden="true">
            <circle cx="12" cy="12" r="12" fill="currentColor"/>
            <path d="M12 6.25v7" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
            <circle cx="12" cy="17.25" r="1.35" fill="#ffffff"/>
          </svg>
          <span class="limit-meter__platform">{{ platformLabel }}</span>
          <span class="limit-meter__nums">{{ counterText }}</span>
        </span>
        <!-- created with the meter, so for ordinary typing it is long-established by 80%. The watcher's
             $nextTick covers the one case that creates and fills it in a single render: a draft
             jumping from empty straight past 80% (paste, template, speech-to-text, AI reply). -->
        <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ limitAnnouncement }}</span>
      </div>
      <template>
        <div class="text-right" v-for="admin in typingAdmins" :key="admin.id">
          <small class="text-muted">{{ `${admin.name} กำลังพิมพ์...` }}</small>
        </div>
      </template>
      <hr/>
    </div>
    <div class="col-12 col-lg-7">
      <div v-if="fileUploads && fileUploads.length === 1">
        <h5>
          <span
            class="badge badge-secondary"
            v-for="(file, index) in fileUploads"
            :key="index"
          >
            {{ file.name | shortText(50) }}
            <span role="button" @click="removeFileUpload(index)">
              <i class="fa fa-times"></i>
            </span>
          </span>
        </h5>
      </div>
      <div class="d-flex align-items-center box-left" v-else>
        <label class="mb-0 mr-3">
          <i class="fas fa-camera icon-message-box"></i>
          <input
            type="file"
            multiple
            ref="imageFiles"
            @change="handleFileUpload('image')"
            hidden
          />
          <!--          <input type="file" ref="fileUpload1" @change="handleFileUpload('image')" hidden>-->
        </label>
        <label class="mb-0 mr-3">
          <i class="fas fa-paperclip icon-message-box"></i>
          <input
            type="file"
            multiple
            ref="pdfFiles"
            @change="handleFileUpload('pdf')"
            hidden
          />
        </label>
        <label class="mb-0 mr-3">
          <i class="fas fa-video icon-message-box"></i>
          <input
            type="file"
            multiple
            ref="mp4Files"
            @change="handleFileUpload('mp4')"
            hidden
          />
        </label>
        <!-- sticker Line -->
        <b-dropdown id="dropdown-2"
                    dropup
                    variant="dark"
                    class="m-md-2"
                    ref="dropdownStickeLine"
                    v-if="selectedConversation.type === 'LineAtConversation' && groupPackageIds">
          <template v-slot:button-content>
            <i class="far fa-smile"></i>
          </template>
          <b-card class="p-0">
            <ul class="nav nav-tabs">
              <li class="nav-item" v-for="(groupPackageId, index) in groupPackageIds" :key="index">
                <a class="nav-link"
                   data-toggle="tab"
                   href="#789"
                   @click.prevent="selectPackageId(groupPackageId.package_id)"
                   :class="{'active' : groupPackageId.package_id === selectedPackageId}">
                  <template>
                    <img :src="groupPackageId.url_img" height="50">
                  </template>
                </a>
              </li>
            </ul>

            <b-card-body style="padding:0px; overflow-y: auto; width:450px; height:200px;">
              <div class="d-flex flex-wrap">
                <div class="flex-item" v-for="(sticker, index) in stickersLine" :key="index" v-show="sticker.package_id === selectedPackageId">
                  <button class="btn btn-link"
                          @click.prevent="selectSticker(sticker)"
                          style="padding:8px;"
                          data-toggle="dropdown">
                    <img :src="sticker.url_img">
                  </button>
                </div>
              </div>
            </b-card-body>
          </b-card>
        </b-dropdown>
        <!-- Message Templates -->
        <b-dropdown
          id="dropdown-dropup"
          dropup
          text="คีย์ลัด"
          class="btn-sm m-0 p-0"
          ref="dropdown">
          <b-card no-body>
            <b-card-header header-tag="nav">
              <div class="row">
                <div class="col align-self-center">
                  <input type="search" class="form-control" @input="debounceSearchKeyword($event.target.value)" placeholder="ค้นหาคำ">
                </div>
                <div class="col-3 text-right align-self-center">
                  <router-link to="/settings/message-template">ตั้งค่า
                  </router-link>
                </div>
              </div>
            </b-card-header>
            <b-card-body
              class="message-template-area text-small p-0"
              @scroll.passive="handleScroll"
            >
              <div
                class="border-bottom"
                v-for="(messageCategory, index) in messageTemplates[`${normalizedCustomerType}`]"
                :key="index + '_' + messageCategory.id">
                <template v-if="messageCategory.message_template && messageCategory.message_template.length > 0">
                  <b-list-group-item variant="light" class="p-1 font-weight-bold text-dark">
                    {{ messageCategory.name }}
                  </b-list-group-item>
                  <div @click="selectMessageTemplate(messageTemplate)" class="row-message-template"
                       v-for="(messageTemplate, key) in messageCategory.message_template" :key="key + '_' + messageTemplate.id">
                    <div class="ml-2 pt-2" v-if="messageTemplate.topic">
                      <span>{{ messageTemplate.topic }}</span>
                    </div>
                    <div class="d-flex justify-content-start align-items-center  p-2 border-bottom">
                      <div class="mr-2">
                        <h5>
                          <span class="badge badge-secondary">/{{ messageTemplate.key }}</span>
                        </h5>
                      </div>
                      <div class="flex-grow-1 text-small">
                        <div v-if="messageTemplate.type === 'text'">
                            <span v-if="messageTemplate.message">
                              {{ messageTemplate.message | shortText(70) }}
                            </span>
                          <span v-else>&nbsp;</span>
                        </div>
                        <div v-else-if="messageTemplate.type === 'image'" style="overflow-x: auto; width:100vh;">
                          <span class="d-inline-block pr-2" v-for="(image, index) in messageTemplate.images" :key="index">
                            <img :src="image.url" alt="" class="rounded" width="" height="50">
                          </span>
                        </div>
                      </div>
                      <div class="float-right" v-if="messageTemplate.attachment">
                        <template v-if="getFileExtension(messageTemplate.attachment) === 'mp4'">
                          <div style="height: 80px;">
                            <vue-plyr :options="{clickToPlay: false, controls: []}">
                              <video
                                :src="messageTemplate.attachment"
                                format="audio/mp4"
                              />
                            </vue-plyr>
                          </div>
                        </template>
                        <template v-else>
                          <img
                            v-lazy="messageTemplate.attachment"
                            alt=""
                            class="rounded"
                            width="60"/>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>

              </div>
            </b-card-body>
          </b-card>
        </b-dropdown>
        <div class="pl-2">
          <button class="btn" :class="isRecording ? 'btn-danger' : 'btn-outline-primary'" @click="checkMic">
            <i class="fa-solid fa-microphone"></i>
            <span v-if="isRecording">&nbsp;&nbsp;กำลังฟัง...</span>
          </button>
        </div>
        <!--        <div class="pl-2" v-if="selectedConversation.customer_type === 'customer'">-->
        <!--          <button class="btn btn-dark"-->
        <!--                  @click="sendCustomerChatTopicTags()">-->
        <!--            <i class="far fa-comment-alt fa-sm"></i> ส่งหัวข้อการสนทนา-->
        <!--          </button>-->
        <!--        </div>-->
      </div>
    </div>
    <div class="col-12 col-lg-5 align-items-center d-flex justify-content-end pr-4 text-right box-right">
      <!-- remind chat -->
      <b-dropdown
        dropup
        variant="warning"
        class="mb-0 mr-3"
        ref="dropdownRemind"
        v-if="['customer', 'professional'].includes(selectedConversation.customer_type)"
      >
        <template v-slot:button-content>
          <i class="fas fa-bell"></i>
          <!-- แสดงเมื่อแชทนั้นมีการตั้งเวลาแจ้งเตือน -->
          <span class="small" style="padding:0px;">
            {{ remindAt | displayRemindDate }}
          </span>
        </template>
        <b-card class="p-0">
          <label>เลือกเวลาการแจ้งเตือน</label>
          <button v-show="selectedConversation.remind_at || remindAt"
                  class="btn btn-link float-right text-danger" @click.prevent="removeReminder" style="padding:0px;">
            <i class="far fa-trash-alt"></i> ลบการแจ้งเตือน
          </button>
          <hr>
          <b-card-body style="padding:0px; width:350px; height:375px;">
            <form @submit.prevent="submitConfirm">
              <div class="form-group">
                <label>เมนูลัด</label>
                <select class="form-control" id="shortcut" name="shortcut" v-model="selectedRemind" @change="onShortcutChange($event)">
                  <option :value="-1">เลือก</option>
                  <option :value="15">อีก 15 นาที</option>
                  <option :value="30">อีก 30 นาที</option>
                  <option :value="45">อีก 45 นาที</option>
                  <option :value="60">อีก 1 ชั่วโมง</option>
                  <option :value="120">อีก 2 ชั่วโมง</option>
                  <option :value="180">อีก 3 ชั่วโมง</option>
                </select>
              </div>
              <div class="form-group">
                <div style="margin-top:2px;">
                  <label>วันที่แจ้งเตือน</label>
                  <datepicker :format="'dd/MM/yyyy'"
                              :inputClass="'form-control'"
                              :placeholder="'dd/mm/yyyy'"
                              v-model="remindDate"
                              ref="remind_date_picker"
                              @input="changeInput"/>
                </div>
              </div>
              <div class="form-group">
                <div style="margin-top:10px;">
                  <label>เวลาที่แจ้งเตือน (ชั่วโมง:นาที)</label>
                  <input type="text" class="form-control" required
                         v-model="remindTime" placeholder="HH:mm (ตัวอย่าง 10:00)">
                </div>
              </div>

              <div class="form-group" style="margin-top:100px;">
                <button class="btn btn-light" type="button" @click.prevent="clearReminder">ล้างค่า</button>
                <button class="btn btn-success" type="submit"
                        style="float:right;">ยืนยัน
                </button>
              </div>
            </form>

          </b-card-body>
        </b-card>
      </b-dropdown>
      <!-- button send message -->
      <button type="button" class="btn btn-info px-4"
              :disabled="isSending || isOverTextLimit"
              :title="isOverTextLimit ? blockedSendDetail : null"
              :aria-describedby="isOverTextLimit ? warningLineId : null"
              @click="sendMessage()">
        {{ isSending ? 'กำลังส่ง...' : 'ส่งข้อความ' }}
      </button>
    </div>
  </div>
</template>

<script>
import { useSpeechRecognition } from '@vueuse/core'
import axios from 'axios'
import { mapActions, mapState } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import moment from 'moment'
import {
  countMessageLength,
  getTextLimit,
  getPlatformLabel,
  getLimitState,
  counterText,
  warningText,
  blockedSendDetail,
  nearLimitAnnouncement
} from '@/constants/messageLimits'

let limitUid = 0

export default {
  props: {
    selectedConversation: Object
  },
  components: {
    Datepicker
  },
  data () {
    return {
      message: null,
      fileUploads: null,
      isSending: false,
      queryParams: {
        page: 0,
        limit: 50,
        customer_type: 'customer',
        show_categories: 1,
        keyword: ''
      },
      searchKeywordTimeout: null,
      typingSignalTimeout: null,
      typingAdminTimeout: {},
      isTyping: false,
      stickersLine: null,
      stickerLine: null,
      selectedPackageId: '11537',
      groupPackageIds: null,
      activePackageId: null,
      typingAdmins: [],
      remindDate: '',
      remindTime: '',
      selectedRemind: -1,
      submitData: '',
      remindAt: '',
      isRecording: false,
      speech: null,
      warningLineId: `limit-warn-${++limitUid}`,
      limitAnnouncement: ''
    }
  },
  computed: {
    ...mapState('admin', ['admin']),
    ...mapState('messageTemplate', ['messageTemplates', 'canLoadMore']),
    ...mapState('conversation', ['customerType']),
    ...mapState('aiAssistant', ['replyTranslateAi']),
    ...mapState('general', ['messageLimits', 'messageLimitLabels']),
    activeTextLimit () {
      const type = this.selectedConversation ? this.selectedConversation.type : null
      return getTextLimit(type, this.messageLimits)
    },
    platformLabel () {
      const type = this.selectedConversation ? this.selectedConversation.type : null
      return getPlatformLabel(type, this.messageLimitLabels)
    },
    textLength () {
      return countMessageLength(this.message)
    },
    limitState () {
      return getLimitState(this.textLength, this.activeTextLimit)
    },
    showLimitMeter () {
      return this.limitState !== 'none' && this.limitState !== 'hidden'
    },
    isOverTextLimit () {
      return this.limitState === 'over'
    },
    counterText () {
      return counterText(this.textLength, this.activeTextLimit)
    },
    warningText () {
      return warningText(this.platformLabel)
    },
    blockedSendDetail () {
      return blockedSendDetail(this.textLength, this.activeTextLimit, this.platformLabel)
    },
    normalizedCustomerType () {
      if (this.customerType === 'professional_secondary' || this.customerType === 'professional') {
        return 'professional'
      }
      return this.customerType
    }
  },
  watch: {
    'selectedConversation.id': function (newVal, oldVal) {
      this.loadMessageTemplates()
    },
    // Announce ONCE on crossing 80%. 'idle' must stay in the clear list — otherwise re-crossing 80%
    // assigns the identical string, re-renders nothing, and never announces again for that draft.
    limitState (newVal, oldVal) {
      if (newVal === 'neutral' && oldVal !== 'neutral' && oldVal !== 'warning') {
        this.$nextTick(() => {
          this.limitAnnouncement = nearLimitAnnouncement(this.activeTextLimit, this.platformLabel)
        })
      } else if (newVal === 'idle' || newVal === 'hidden' || newVal === 'none' || newVal === 'over') {
        this.limitAnnouncement = ''
      }
    },
    message (newVal, oldVal) {
      if (newVal) {
        this.debounceSendTypingSignal()
      }
    },
    'speech.result': function (newVal, oldVal) {
      this.message = newVal
    },
    'speech.isListening': function (newVal, oldVal) {
      if (newVal === false) {
        this.stopSpeechRecognition()
        this.isRecording = false
      }
    },
    replyTranslateAi () {
      if (this.replyTranslateAi) {
        this.message = this.replyTranslateAi
      }
    }
  },
  filters: {
    onlyTime: function (date) {
      if (!date) {
        return
      }
      return moment(date).format('HH:mm')
    },
    displayRemindDate: function (date) {
      let today = moment().format('YYYY-MM-DD')
      let onlyDate = moment(date).format('YYYY-MM-DD')
      if (!date) {
        return
      }
      if (today === onlyDate) {
        return moment(date).format('HH:mm')
      } else {
        return moment(date).format('YYYY-MM-DD HH:mm')
      }
    }
  },
  methods: {
    ...mapActions('messageTemplate', ['fetchMessageTemplates']),
    getFileExtension (url) {
      return url.split(/[#?]/)[0].split('.').pop().trim()
    },
    checkMic () {
      this.isRecording = !this.isRecording
      if (this.isRecording) {
        this.$refs.message.focus()
        this.startSpeechRecognition()
      } else {
        this.stopSpeechRecognition()
      }
    },
    startSpeechRecognition () {
      if (this.speech && this.speech.isSupported) {
        this.speech.start()
      }
    },
    stopSpeechRecognition () {
      if (this.speech) {
        this.speech.stop()
      }
    },
    async removeReminder () {
      this.$swal({
        text: 'คุณต้องการลบเตือนนัดหมายของบทสนทนานี้ ใช่หรือไม่ ?',
        showCancelButton: true,
        confirmButtonText: 'ใช่ ',
        cancelButtonText: 'ไม่'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios.delete('/conversations/' + this.selectedConversation.id + '/reminder?admin_id=' + this.admin.id)
            this.remindAt = ''
            this.$swal({
              text: 'ลบการแจ้งเตือนสำเร็จ',
              type: 'success'
            })
          } catch (err) {
            this.$swal({
              text: 'ผิดพลาด ' + err.message,
              type: 'error'
            })
          }
        }
      })
    },
    async submitConfirm () {
      let submitData = {}
      submitData.remind_date = moment(this.remindDate).format('YYYY-MM-DD')
      if (this.remindTime) {
        submitData.remind_date += ` ${this.remindTime}:00`
      }

      let checkIsValidDate = moment(submitData.remind_date, 'YYYY-MM-DD HH:mm:ss', true).isValid()
      if (!checkIsValidDate) {
        this.$swal({
          text: 'รูปแบบการตั้งเวลาไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง',
          type: 'error'
        })
        return false
      }

      let checkIsAfterToday = moment().isSameOrAfter(submitData.remind_date)
      if (checkIsAfterToday) {
        this.$swal({
          text: 'ไม่สามารถตั้งเวลาเตือนย้อนหลังได้',
          type: 'error'
        })
        return false
      }

      if (!this.remindDate) {
        this.$refs.remind_date_picker.$el.querySelector('input').focus()
        this.$refs.remind_date_picker.showCalendar()
        return false
      }

      if (!this.remindDate && !this.remindTime) {
        submitData.remind_date = ''
      }
      const { data } = await axios.post(`/conversations/${this.selectedConversation.id}/reminder`, {
        remind_at: submitData.remind_date,
        admin_id: this.admin.id
      })
      if (!data.error) {
        this.remindAt = data.conversation.remind_at
        if (data.conversation.remind_at == null) {
          this.$swal({
            text: 'ยกเลิกการแจ้งเตือน',
            type: 'success'
          })
        } else {
          this.$swal({
            text: 'ตั้งเวลาแจ้งเตือนสำเร็จ',
            type: 'success'
          })
        }
        this.$refs.dropdownRemind.hide(true)
      } else {
        this.$swal({
          text: 'ผิดพลาด',
          type: 'error'
        })
      }
    },
    clearReminder () {
      this.selectedRemind = -1
      this.remindDate = ''
      this.remindTime = ''
    },
    onShortcutChange (event) {
      const minutes = event.target.value
      // console.log('minutes', minutes)
      if (minutes < 0) {
        this.remindDate = ''
        this.remindTime = ''
        return
      }
      let datetime = moment().add(minutes, 'minutes').format('YYYY-MM-DD HH:mm')
      const comp = datetime.split(' ')
      this.remindDate = comp[0]
      this.remindTime = comp[1]
    },
    changeInput () {
      this.selectedRemind = -1
      this.remindTime = ''
    },
    async searchKeyword (keyword) {
      const queryParams = { ...this.queryParams }
      queryParams.force_fetch_data = true
      queryParams.keyword = keyword
      queryParams.page = 0
      await this.fetchMessageTemplates(queryParams)
      this.$forceUpdate()
    },
    selectPackageId (packageId) {
      this.selectedPackageId = packageId
    },
    selectSticker (sticker) {
      this.stickerLine = sticker
      this.sendMessage()
      this.$refs.dropdownStickeLine.hide(true)
    },
    async fetchStickers () {
      const { data } = await axios.get('/sticker-line')
      this.stickersLine = data.stickers
      this.groupPackageIds = data.groupPackageIds
    },
    clearMessage () {
      this.message = null
      this.fileUploads = null
      this.stickerLine = null
      this.isSending = false
      this.queryParams = {
        page: 0,
        limit: 50,
        // keep the conversation's type: this can run after loadMessageTemplates has set it, and a
        // hardcoded 'customer' would send every later search and load-more to the customer list
        customer_type: this.selectedConversation ? this.selectedConversation.customer_type : 'customer',
        show_categories: 1,
        keyword: ''
      }
    },
    debounceSearchKeyword (keyword) {
      if (this.searchKeywordTimeout) clearTimeout(this.searchKeywordTimeout)
      this.searchKeywordTimeout = setTimeout(() => {
        this.searchKeyword(keyword)
      }, 400)
    },
    debounceSendTypingSignal () {
      if (this.isTyping) {
        return
      }
      this.isTyping = true
      this.sendTypingSignal()
      if (this.typingSignalTimeout) clearTimeout(this.typingSignalTimeout)
      this.typingSignalTimeout = setTimeout(() => {
        this.isTyping = false
      }, 5000)
    },
    async sendTypingSignal () {
      await axios.post(`/conversations/${this.selectedConversation.id}/typing`, { admin_id: this.admin.id })
      return Promise.resolve(1)
    },
    getExtension (filename) {
      const parts = filename.split('.')
      return parts[parts.length - 1]
    },
    isImage (ext) {
      switch (ext.toLowerCase()) {
        case 'jpg':
        case 'gif':
        case 'jpeg':
        case 'png':
          return true
      }
      return false
    },
    removeFileUpload (index) {
      this.fileUploads = null
    },
    handleFileUpload (fileType) {
      let valid = true
      // fileType === 'image' as default
      this.fileUploads = this.$refs.imageFiles.files
      if (fileType === 'pdf') {
        this.fileUploads = this.$refs.pdfFiles.files
      } else if (fileType === 'mp4') {
        this.fileUploads = this.$refs.mp4Files.files
      }
      const fileCount = this.fileUploads.length
      // Validate file upload
      for (const file of this.fileUploads) {
        const ext = this.getExtension(file.name)
        switch (fileType) {
          case 'image':
            if (!this.isImage(ext)) {
              valid = false
            }
            break
          case 'pdf':
            if (ext !== 'pdf') {
              valid = false
            }
            break
          case 'mp4':
            if (ext !== 'mp4') {
              valid = false
            }
            break
        }
      }

      if (valid) {
        if (fileCount > 1) {
          // Send multi-files upload
          this.sendFileUploads()
        }
      } else {
        this.fileUploads = null
        this.$swal({ text: 'ประเภทของไฟล์ไม่ถูกต้อง', icon: 'error' })
      }
    },
    async sendFileUploads () {
      try {
        for (const file of this.fileUploads) {
          let formData = new FormData()
          formData.append('fileUpload', file)
          formData.append(
            'conversation',
            JSON.stringify(this.selectedConversation)
          )
          formData.append('adminId', this.admin.id)
          const ext = this.getExtension(file.name)
          // Upload file to server
          let url = '/messages/upload'
          if (ext === 'pdf') {
            url = '/messages/upload-pdf'
          } else if (ext === 'mp4') {
            url = '/messages/upload-mp4'
          }
          await axios.post(url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
        }
      } catch (e) {
        this.$swal({ text: 'ไม่สามารถส่งข้อความได้', icon: 'error' })
        console.log('check error', e)
      }
      this.fileUploads = null
    },
    async sendMessage () {
      // Covers the send button AND Shift+Enter, before isSending and before any axios call. It never
      // touches this.message / this.stickerLine / this.fileUploads, so the draft stays put.
      if (this.isOverTextLimit) {
        this.$swal({ title: 'ส่งไม่ได้', text: this.blockedSendDetail, icon: 'error' })
        return
      }
      this.isSending = true
      try {
        // Send file uploads
        if (this.fileUploads) {
          await this.sendFileUploads()
        }
        // Send message
        if (this.message || this.stickerLine) {
          let url = '/messages'
          const submitData = {
            conversation: this.selectedConversation,
            adminId: this.admin.id
          }
          if (this.stickerLine) {
            url = '/messages/sticker-line'
            submitData.stickerLine = this.stickerLine
          } else {
            if (this.isMessageTemplate()) {
              url = '/messages/template'
            }
            submitData.message = this.message
          }
          const res = await axios.post(url, submitData)
          if (res.status === 200) {
            this.message = null
            this.stickerLine = null
          }
        }
      } catch (e) {
        console.log('check error', e.message)
        // the server's own error string already names the real platform, limit and length
        const res = e.response
        if (res && res.status === 400 && res.data && res.data.reason === 'MESSAGE_TOO_LONG') {
          this.$swal({ title: 'ส่งไม่ได้', text: res.data.error, icon: 'error' })
        } else {
          this.$swal({ text: 'ไม่สามารถส่งข้อความได้', icon: 'error' })
        }
      }
      this.isSending = false
    },
    isMessageTemplate () {
      const reg = /^\/\d+$/
      const result = this.message.match(reg)
      return !!result
    },
    selectMessageTemplate (messageTemplate) {
      if (messageTemplate.type === 'text') {
        this.message = `${messageTemplate.message}`
        if (messageTemplate.attachment) {
          this.message = `/${messageTemplate.key}`
        }
      } else {
        this.message = `/${messageTemplate.key}`
      }

      this.$refs.dropdown.hide(true)
    },
    handleScroll ({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollTop + clientHeight >= scrollHeight) {
        // load more conversation
        this.queryParams.page += 1
        this.fetchMessageTemplates(this.queryParams)
      }
    },
    loadMessageTemplates () {
      // Set customer type based on conversation
      this.queryParams.customer_type = this.selectedConversation
        ? this.selectedConversation.customer_type
        : 'customer'
      this.fetchMessageTemplates(this.queryParams)
      if (this.selectedConversation) {
        this.remindAt = this.selectedConversation.remind_at
        this.remindDate = ''
        this.remindTime = ''
        if (this.selectedConversation.remind_at) {
          this.remindDate = moment(this.selectedConversation.remind_at).format('YYYY-MM-DD')
          this.remindTime = moment(this.selectedConversation.remind_at).format('HH:mm')
        }
      }
    },
    async sendCustomerChatTopicTags () {
      const submitData = {
        conversation: this.selectedConversation,
        adminId: this.admin.id
      }
      await axios.post('/messages/buttons', submitData)
    }
  },
  mounted () {
    this.loadMessageTemplates()
    this.speech = useSpeechRecognition({
      lang: 'th-TH',
      interimResults: true,
      continuous: true
    })
  },
  created () {
    this.fetchStickers()
  },
  sockets: {
    connect () {
      // console.log('socket connected')
    },
    disconnect () {
      console.log('socket disconnected')
    },
    onTyping (data) {
      if (!this.selectedConversation || Number(this.selectedConversation.id) !== Number(data.conversationId)) {
        return
      }
      const admin = data.admin
      if (this.admin.id === admin.id) {
        return
      }
      let typingAdmin = this.typingAdmins.find(item => item.id === admin.id)
      if (!typingAdmin) {
        this.typingAdmins.push(admin)
      }
      if (this.typingAdminTimeout[admin.id]) clearTimeout(this.typingAdminTimeout[admin.id])
      this.typingAdminTimeout[admin.id] = setTimeout(() => {
        this.typingAdmins = this.typingAdmins.filter(item => item.id !== admin.id)
      }, 3000)
    }
  }
}
</script>
<style lang="scss" scoped>
.flex-item {
  background-color: #ffffff;

  img {
    width: 70px;
    object-fit: contain;
  }
}

div.box-left, div.box-right {
  zoom: 0.8;
}

// Own hex values, not bootstrap .text-warning (#ffc107, 1.6:1) or .text-danger — neither is
// accessible at 12px.
.limit-meter {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px 12px;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.3;
  color: #667085;

  // Empty on purpose: 'idle' is styled like 'neutral', already the quietest legible treatment — the
  // one axis left, a lighter grey, fails AA. The hook keeps the band assertable.
  &--idle    { }
  &--warning { color: #B54708; font-weight: 600; }
  &--over    { color: #B42318; font-weight: 600; }
}
.limit-meter__warn {
  flex: 1 1 auto;
  text-align: left;
}
.limit-meter__count {
  margin-left: auto;
  text-align: right;
}
.limit-meter__glyph {
  vertical-align: -2px;
  margin-right: 4px;
}
.limit-meter__nums {
  margin-left: 4px;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .limit-meter { font-size: 11px; }
}

// bootstrap's .btn:disabled only dims; the cursor must say the control is dead too
.btn.btn-info.px-4:disabled {
  cursor: not-allowed;
}

</style>
